// Gmail and Google Calendar for VERA Studio on the web, with the person's own Google account (delegated, in this page).
// - Google's own sign-in window (Google Identity Services). The access token stays in this tab's memory and session
//   storage only, and expires after about an hour; reconnecting is one click.
// - The operations have the same names and shapes as the Microsoft ones (m365ops.ts), so everything in Studio that reads
//   email or the calendar, sends, drafts or books works with Gmail and Google Calendar through the same graph() call.
// - Sending, drafting and booking always ask first (the same confirmation as Outlook).

import { auth, devMode } from "../auth/auth";
import { desktop } from "./desktop";
import { settingsChanged } from "./history";

const CLIENT_ID = ((import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? "").trim();
export const GOOGLE_SCOPES = [
  "openid", "email",
  "https://www.googleapis.com/auth/gmail.readonly",
  "https://www.googleapis.com/auth/gmail.compose",
  "https://www.googleapis.com/auth/calendar.events",
  "https://www.googleapis.com/auth/calendar.freebusy",
].join(" ");

/** True where Google can be connected: the web app with real sign-in and a Google client set up. */
export const googleAvailable = () => !desktop && !devMode && auth.kind === "microsoft" && CLIENT_ID.length > 0;
export const googleSetUp = () => CLIENT_ID.length > 0;

type Token = { value: string; exp: number; email: string };
let person = "";
let token: Token | null = null;
const tokenKey = () => `vera-google-token-${person}`;
const onKey = (u: string) => `vera-google-on-${u}`;

/** Called when the signed-in person is known (from useM365). */
export function setGooglePerson(userId: string) {
  if (person === userId) return;
  person = userId;
  token = null;
  try { const raw = sessionStorage.getItem(tokenKey()); if (raw) token = JSON.parse(raw) as Token; } catch { /* ignore */ }
}

/** Whether this person chose to connect Google (on any device; synced with their settings). */
export const googleChosen = () => { try { return localStorage.getItem(onKey(person)) === "1"; } catch { return false; } };
/** Connected now: chosen, and a token that hasn't expired. */
export const googleConnected = () => googleChosen() && Boolean(token && token.exp > Date.now() + 60_000);
export const googleEmail = () => token?.email ?? "";

function setChosen(on: boolean) {
  try { if (on) localStorage.setItem(onKey(person), "1"); else localStorage.removeItem(onKey(person)); } catch { /* ignore */ }
  settingsChanged();
  window.dispatchEvent(new Event("vera-m365"));
}

// ---------- Google's sign-in window ----------
type TokenResponse = { access_token?: string; expires_in?: number; error?: string; error_description?: string };
type TokenClient = { requestAccessToken: (o?: { prompt?: string; login_hint?: string }) => void };
type Gis = { accounts: { oauth2: { initTokenClient: (c: { client_id: string; scope: string; callback: (r: TokenResponse) => void; error_callback?: (e: { type?: string; message?: string }) => void }) => TokenClient; revoke: (t: string, done?: () => void) => void } } };
declare global { interface Window { google?: Gis } }

let gisLoading: Promise<Gis> | null = null;
function loadGis(): Promise<Gis> {
  return (gisLoading ??= new Promise<Gis>((resolve, reject) => {
    if (window.google?.accounts?.oauth2) { resolve(window.google); return; }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => (window.google?.accounts?.oauth2 ? resolve(window.google) : reject(new Error("Google sign-in didn't load.")));
    s.onerror = () => { gisLoading = null; reject(new Error("Google sign-in couldn't load. Check your connection.")); };
    document.head.appendChild(s);
  }));
}

/** Opens Google's window (must follow a click). Resolves with a fresh token. */
export async function connectGoogle(): Promise<string> {
  if (!googleAvailable()) throw new Error(desktop ? "Connect Gmail and Google Calendar in the web version of VERA Studio for now." : "Google isn't set up yet.");
  const gis = await loadGis();
  const r = await new Promise<TokenResponse>((resolve, reject) => {
    const client = gis.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID, scope: GOOGLE_SCOPES,
      callback: resolve,
      error_callback: (e) => reject(new Error(e.type === "popup_closed" ? "Cancelled." : e.type === "popup_failed_to_open" ? "Your browser blocked Google's window. Please allow pop-ups for VERA and try again." : "Google didn't connect. Please try again.")),
    });
    // Send only what's set: an empty account hint makes Google refuse the request as malformed.
    client.requestAccessToken(token?.email ? { login_hint: token.email } : { prompt: "consent" });
  });
  if (!r.access_token) throw new Error(r.error === "access_denied" ? "Google access wasn't allowed." : "Google didn't connect. Please try again.");
  let email = "";
  try { email = String(((await (await fetch("https://www.googleapis.com/oauth2/v3/userinfo", { headers: { Authorization: `Bearer ${r.access_token}` } })).json()) as { email?: string }).email ?? ""); } catch { /* optional */ }
  token = { value: r.access_token, exp: Date.now() + (r.expires_in ?? 3600) * 1000, email };
  try { sessionStorage.setItem(tokenKey(), JSON.stringify(token)); } catch { /* ignore */ }
  setChosen(true);
  return token.value;
}

export function disconnectGoogle() {
  if (token) { try { window.google?.accounts.oauth2.revoke(token.value); } catch { /* ignore */ } }
  token = null;
  try { sessionStorage.removeItem(tokenKey()); } catch { /* ignore */ }
  setChosen(false);
}

/** The current token, or a plain message asking to reconnect (Google's window can only open after a click). */
export function googleToken(): string {
  if (token && token.exp > Date.now() + 60_000) return token.value;
  throw new Error("Your Gmail connection has timed out. Reconnect Gmail in Connections, it takes one click.");
}
